import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import VideoCard from "@/components/feature/VideoCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { searchVideos } from "@/utils/video";

const typeFilters = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
] as const;

type TypeFilter = (typeof typeFilters)[number]["key"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  usePageMeta(
    query ? `“${query}”的搜索结果 - 91视频免费看` : "搜索影视 - 91视频免费看",
    query
      ? `在 91视频免费看搜索“${query}”相关的电影与电视剧资源，快速找到你想看的作品。`
      : "在 91视频免费看搜索电影、电视剧资源。"
  );

  const results = useMemo(
    () => searchVideos(query, typeFilter),
    [query, typeFilter]
  );

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = keyword.trim();
    setSearchParams(trimmed ? { q: trimmed } : {});
  };

  const hasQuery = query.trim().length > 0;

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-10">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500 mb-4" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
        <span className="text-foreground-800">搜索</span>
      </nav>

      <header className="mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">搜索影视</h1>
        <p className="mt-2 text-sm text-foreground-600">输入片名、类型或演员关键词，快速找到想看的影视作品。</p>
      </header>

      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 max-w-2xl">
        <div className="relative flex-1">
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base"></i>
          </span>
          <input
            type="text"
            name="q"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="例如：流浪地球、悬疑、科幻"
            className="w-full pl-11 pr-4 py-3 text-sm rounded-full bg-background-100 border border-background-200 text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
          />
        </div>
        <button
          type="submit"
          className="px-7 py-3 rounded-full bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          搜索
        </button>
      </form>

      {hasQuery ? (
        <div className="mt-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <p className="text-sm text-foreground-600">
              <span className="text-foreground-400">关键词：</span>
              <span className="font-semibold text-primary-600">{query}</span>
              <span className="ml-2 text-foreground-400">共 {results.length} 个结果</span>
            </p>
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
              {typeFilters.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setTypeFilter(option.key)}
                  className={`px-4 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                    typeFilter === option.key ? "bg-primary-500 text-white" : "text-foreground-600 hover:text-primary-600"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {results.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {results.map((item) => (
                <VideoCard key={item.id} item={item} showTypeBadge />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-emotion-unhappy-line text-3xl"></i>
              </span>
              <h2 className="font-heading text-lg font-semibold text-foreground-900">没有找到相关结果</h2>
              <p className="mt-2 text-sm text-foreground-500">
                换个关键词试试，或浏览全部电影与电视剧资源。
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link
                  to="/movie"
                  className="px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  浏览电影
                </Link>
                <Link
                  to="/tv"
                  className="px-5 py-2.5 rounded-full bg-background-100 border border-background-200 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap"
                >
                  浏览电视剧
                </Link>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-10 rounded-lg bg-primary-50 border border-primary-100 p-6 md:p-10">
          <h2 className="font-heading text-lg font-bold text-primary-900">热门搜索</h2>
          <div className="mt-4 flex flex-wrap gap-2.5">
            {["流浪地球2", "狂飙", "三体", "奥本海默", "悬疑", "科幻", "爱情", "古装"].map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => setSearchParams({ q: tag })}
                className="px-4 py-2 rounded-full bg-background-50 border border-primary-200 text-sm text-primary-700 hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}